import { useState } from 'react';
import { Modal, StatusBadge } from '../common';
import { STATUS_META } from '../../utils/constants';
import { updateRestaurant } from '../../services/restaurantService';

/**
 * Workflow Review Modal
 * Role-specific review and status advancement
 * Supports: data_entry, sales_manager, admin, accountant, rep
 */
export function WorkflowReviewModal({
  isOpen,
  onClose,
  restaurant,
  currentUser,
  showToast,
  onUpdateSuccess,
}) {
  if (!restaurant) return null;

  const role = currentUser?.role;
  const status = restaurant.status;

  const [returnNote, setReturnNote] = useState('');
  const [showReturnInput, setShowReturnInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Determine available actions based on role + status
  const getActions = () => {
    if (role === 'data_entry' && status === 'data_review') {
      return {
        primary: { label: '📤 إرسال لمراجعة المدير', nextStatus: 'ready_for_rep', color: '#3b82f6' },
        title: 'مراجعة بيانات المطعم',
        subtitle: 'تحقق من البيانات ثم أرسلها لمدير المبيعات والأدمن للمراجعة',
      };
    }
    if ((role === 'sales_manager' || role === 'admin') && status === 'ready_for_rep') {
      return {
        primary: { label: '✅ اعتماد وإرسال للمحاسب', nextStatus: 'invoice_pending', color: '#7c3aed' },
        returnTo: 'new',
        title: 'مراجعة المدير',
        subtitle: 'راجع بيانات المطعم واعتمدها أو أرجعها للتصحيح',
      };
    }
    if (role === 'accountant' && status === 'invoice_pending') {
      return {
        primary: { label: '🚚 اعتماد وإرسال للمندوب', nextStatus: 'in_field', color: '#f59e0b' },
        returnTo: 'new',
        title: 'مراجعة المحاسب',
        subtitle: 'راجع الفاتورة واعتمدها أو أرجعها للتصحيح',
      };
    }
    if (role === 'rep' && status === 'in_field') {
      return {
        primary: { label: '📋 إرسال للإغلاق', nextStatus: 'pending_closure', color: '#ec4899' },
        title: 'مهمة المندوب',
        subtitle: 'أكمل المهمة وأرسلها للإغلاق النهائي',
      };
    }
    if ((role === 'sales_manager' || role === 'admin') && status === 'pending_closure') {
      return {
        primary: { label: '🎉 إغلاق الصفقة', nextStatus: 'deal_closed', color: '#10b981' },
        returnTo: 'in_field',
        title: 'إغلاق الصفقة',
        subtitle: 'راجع التقرير النهائي وأغلق الصفقة أو أرجعها للمندوب',
      };
    }
    return {
      title: 'عرض المطعم',
      subtitle: 'لا توجد إجراءات متاحة لهذا الدور والحالة',
    };
  };

  const actions = getActions();

  const handleAdvance = async () => {
    if (!actions.primary) return;
    setSubmitting(true);
    try {
      await updateRestaurant(restaurant.id, {
        status: actions.primary.nextStatus,
        reviewed_by: currentUser.id,
        reviewed_at: new Date().toISOString(),
      }, currentUser);
      showToast(`✅ تم ${actions.primary.label.replace(/[^\u0600-\u06FF\s]/g, '').trim()} بنجاح`);
      onUpdateSuccess?.();
      onClose();
    } catch (err) {
      console.error('Advance error:', err);
      showToast('❌ فشل في تحديث الحالة', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturn = async () => {
    if (!returnNote.trim()) {
      showToast('❌ يرجى كتابة سبب الإرجاع', 'error');
      return;
    }
    setSubmitting(true);
    try {
      await updateRestaurant(restaurant.id, {
        status: actions.returnTo,
        return_note: returnNote.trim(),
        returned_by: currentUser.id,
        returned_at: new Date().toISOString(),
      }, currentUser);
      showToast('✅ تم إرجاع المطعم للتصحيح');
      onUpdateSuccess?.();
      onClose();
    } catch (err) {
      console.error('Return error:', err);
      showToast('❌ فشل في إرجاع المطعم', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const files = restaurant.files || {};
  const hasFiles = Object.values(files).some(Boolean);

  return (
    <Modal title={actions.title} isOpen={isOpen} onClose={onClose} size="lg">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Subtitle */}
        <p style={{ color: '#64748b', fontSize: 13, margin: 0 }}>
          {actions.subtitle}
        </p>

        {/* Restaurant Summary Card */}
        <div style={{
          background: '#f8fafc',
          borderRadius: 12,
          padding: 16,
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, color: '#000000', fontSize: 18, fontWeight: 900 }}>
              🍽️ {restaurant.name}
            </h3>
            <StatusBadge status={status} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            {restaurant.ownerName && (
              <InfoPill icon="👤" label="المالك" value={restaurant.ownerName} />
            )}
            {restaurant.ownerPhone && (
              <InfoPill icon="📞" label="هاتف المالك" value={restaurant.ownerPhone} />
            )}
            {restaurant.phone && (
              <InfoPill icon="☎️" label="هاتف المطعم" value={restaurant.phone} />
            )}
            {restaurant.area && (
              <InfoPill icon="📍" label="المنطقة" value={restaurant.area} />
            )}
            {restaurant.taxNumber && (
              <InfoPill icon="🧾" label="الرقم الضريبي" value={restaurant.taxNumber} />
            )}
            {restaurant.commissionPercentage !== undefined && (
              <InfoPill icon="💎" label="العمولة" value={`${restaurant.commissionPercentage}%`} />
            )}
          </div>
        </div>

        {/* Files Section */}
        {hasFiles && (
          <div>
            <h4 style={{ color: '#000000', fontSize: 14, fontWeight: 700, margin: '0 0 10px' }}>
              📎 الملفات المرفقة
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {files.logoUrl && <FileTag label="الشعار" url={files.logoUrl} />}
              {files.menu1Url && <FileTag label="القائمة 1" url={files.menu1Url} />}
              {files.menu2Url && <FileTag label="القائمة 2" url={files.menu2Url} />}
              {files.tradeLicenseUrl && <FileTag label="الرخصة التجارية" url={files.tradeLicenseUrl} />}
              {files.ownerIdUrl && <FileTag label="هوية المالك" url={files.ownerIdUrl} />}
              {files.invoiceUrl && <FileTag label="الفاتورة" url={files.invoiceUrl} />}
              {files.paymentReceiptUrl && <FileTag label="إيصال الدفع" url={files.paymentReceiptUrl} />}
            </div>
          </div>
        )}

        {/* Return Note Input */}
        {showReturnInput && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ color: '#000000', fontSize: 13, fontWeight: 700 }}>
              📝 سبب الإرجاع:
            </label>
            <textarea
              value={returnNote}
              onChange={e => setReturnNote(e.target.value)}
              placeholder="اكتب سبب إرجاع المطعم للتصحيح..."
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontFamily: "'Cairo', sans-serif",
                fontSize: 13,
                minHeight: 80,
                resize: 'vertical',
                direction: 'rtl',
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowReturnInput(false); setReturnNote(''); }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #e2e8f0',
                  background: '#ffffff',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                إلغاء
              </button>
              <button
                onClick={handleReturn}
                disabled={submitting}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#ef4444',
                  color: '#ffffff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                {submitting ? 'جاري الإرسال...' : '✅ تأكيد الإرجاع'}
              </button>
            </div>
          </div>
        )}

        {/* Workflow Progress */}
        <WorkflowProgress currentStatus={status} />

        {/* Action Buttons */}
        {!showReturnInput && (
          <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'center',
            flexWrap: 'wrap',
            paddingTop: 10,
            borderTop: '1px solid #e2e8f0',
          }}>
            {actions.primary && (
              <button
                onClick={handleAdvance}
                disabled={submitting}
                style={{
                  padding: '12px 28px',
                  borderRadius: 10,
                  border: 'none',
                  background: actions.primary.color,
                  color: '#ffffff',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  fontSize: 14,
                  fontWeight: 900,
                  opacity: submitting ? 0.6 : 1,
                  boxShadow: `0 4px 14px ${actions.primary.color}44`,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { if (!submitting) e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {submitting ? 'جاري المعالجة...' : actions.primary.label}
              </button>
            )}

            {actions.returnTo !== undefined && (
              <button
                onClick={() => setShowReturnInput(true)}
                disabled={submitting}
                style={{
                  padding: '12px 24px',
                  borderRadius: 10,
                  border: '1px solid #ef4444',
                  background: '#ffffff',
                  color: '#ef4444',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Cairo', sans-serif",
                  fontSize: 14,
                  fontWeight: 700,
                  opacity: submitting ? 0.6 : 1,
                }}
              >
                🔙 إرجاع للتصحيح
              </button>
            )}

            {!actions.primary && !actions.returnTo && (
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                لا توجد إجراءات متاحة لهذا الدور في هذه المرحلة
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── SUB-COMPONENTS ────────────────────────────────────────────────

function InfoPill({ icon, label, value }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '8px 12px',
      background: '#ffffff',
      borderRadius: 8,
      border: '1px solid #e2e8f0',
    }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#000000', fontWeight: 700 }}>{value}</div>
      </div>
    </div>
  );
}

function FileTag({ label, url }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 12px',
        background: '#f0fdfa',
        border: '1px solid #4ecdc444',
        borderRadius: 20,
        color: '#4ecdc4',
        fontSize: 12,
        fontWeight: 700,
        textDecoration: 'none',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#4ecdc4'; e.currentTarget.style.color = '#ffffff'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#f0fdfa'; e.currentTarget.style.color = '#4ecdc4'; }}
    >
      📄 {label}
    </a>
  );
}

function WorkflowProgress({ currentStatus }) {
  const stages = [
    { key: 'new', label: 'جديد' },
    { key: 'data_review', label: 'مراجعة البيانات' },
    { key: 'ready_for_rep', label: 'مراجعة المدير' },
    { key: 'invoice_pending', label: 'بانتظار الفاتورة' },
    { key: 'in_field', label: 'في الميدان' },
    { key: 'pending_closure', label: 'بانتظار الإغلاق' },
    { key: 'deal_closed', label: 'مغلق' },
  ];

  const currentIndex = stages.findIndex(s => s.key === currentStatus);
  const isClosed = currentStatus === 'deal_closed';

  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8, fontWeight: 700 }}>
        مسار العمل:
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        {stages.map((stage, i) => {
          const isActive = i === currentIndex;
          const isPast = i < currentIndex;
          const isFuture = i > currentIndex;

          return (
            <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{
                padding: '4px 10px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                background: isClosed ? '#d1fae5' : isActive ? STATUS_META[stage.key]?.bg || '#dbeafe' : isPast ? '#f1f5f9' : '#f8fafc',
                color: isClosed ? '#10b981' : isActive ? STATUS_META[stage.key]?.color || '#3b82f6' : isPast ? '#94a3b8' : '#cbd5e1',
                border: `1px solid ${isClosed ? '#10b98144' : isActive ? (STATUS_META[stage.key]?.color || '#3b82f6') + '44' : isPast ? '#e2e8f0' : '#e2e8f0'}`,
              }}>
                {isClosed ? '✅' : isActive ? '●' : isPast ? '✓' : '○'} {stage.label}
              </span>
              {i < stages.length - 1 && (
                <span style={{ color: isPast ? '#94a3b8' : '#e2e8f0', fontSize: 12 }}>→</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
